import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AdjustmentNotes = ({ adjustments, methodology }) => {
  const [expandedSection, setExpandedSection] = useState('methodology');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const getAdjustmentIcon = (type) => {
    switch (type) {
      case 'positive': return 'TrendingUp';
      case 'negative': return 'TrendingDown';
      default: return 'Minus';
    }
  };

  const getAdjustmentColor = (type) => {
    switch (type) {
      case 'positive': return 'text-success';
      case 'negative': return 'text-error';
      default: return 'text-text-secondary';
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg shadow-card">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-text-primary flex items-center space-x-2">
          <Icon name="FileText" size={20} />
          <span>Valuation Methodology & Adjustments</span>
        </h2>
      </div>
      <div className="divide-y divide-border">
        {/* Methodology Section */}
        <div className="p-6">
          <button
            onClick={() => toggleSection('methodology')}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
              <Icon name="Settings" size={18} />
              <span>Valuation Methodology</span>
            </h3>
            <Icon 
              name={expandedSection === 'methodology' ? 'ChevronUp' : 'ChevronDown'} 
              size={20} 
              className="text-text-secondary" 
            />
          </button>
          
          {expandedSection === 'methodology' && (
            <div className="mt-4 space-y-4">
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-medium text-text-primary mb-2">Base Valuation Process</h4>
                <p className="text-sm text-text-secondary leading-relaxed">
                  {methodology?.baseProcess}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-2 flex items-center space-x-2">
                    <Icon name="Database" size={16} />
                    <span>Data Sources</span>
                  </h4>
                  <ul className="text-sm text-text-secondary space-y-1">
                    {methodology?.dataSources?.map((source, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <Icon name="Check" size={12} className="text-success" />
                        <span>{source}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-2 flex items-center space-x-2">
                    <Icon name="Calculator" size={16} />
                    <span>Weighting Factors</span>
                  </h4>
                  <ul className="text-sm text-text-secondary space-y-1">
                    {methodology?.weightingFactors?.map((factor, index) => (
                      <li key={index} className="flex items-center justify-between">
                        <span>{factor?.name}</span>
                        <span className="font-medium">{factor?.weight}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Adjustments Section */}
        <div className="p-6">
          <button
            onClick={() => toggleSection('adjustments')}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
              <Icon name="Sliders" size={18} />
              <span>Valuation Adjustments</span>
            </h3>
            <Icon 
              name={expandedSection === 'adjustments' ? 'ChevronUp' : 'ChevronDown'} 
              size={20} 
              className="text-text-secondary" 
            />
          </button>
          
          {expandedSection === 'adjustments' && (
            <div className="mt-4 space-y-3">
              {adjustments?.map((adjustment, index) => (
                <div key={index} className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={getAdjustmentIcon(adjustment?.type)} 
                        size={16} 
                        className={getAdjustmentColor(adjustment?.type)} 
                      />
                      <span className="font-medium text-text-primary">{adjustment?.factor}</span>
                    </div>
                    <span className={`font-semibold ${getAdjustmentColor(adjustment?.type)}`}>
                      {adjustment?.type === 'positive' ? '+' : ''}{adjustment?.impact}%
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary">{adjustment?.explanation}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Confidence Factors Section */}
        <div className="p-6">
          <button
            onClick={() => toggleSection('confidence')}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
              <Icon name="Shield" size={18} />
              <span>Confidence Score Factors</span>
            </h3>
            <Icon 
              name={expandedSection === 'confidence' ? 'ChevronUp' : 'ChevronDown'} 
              size={20} 
              className="text-text-secondary" 
            />
          </button>
          
          {expandedSection === 'confidence' && (
            <div className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-3 flex items-center space-x-2">
                    <Icon name="Target" size={16} />
                    <span>Positive Factors</span>
                  </h4>
                  <ul className="space-y-2">
                    {methodology?.confidenceFactors?.positive?.map((factor, index) => (
                      <li key={index} className="flex items-center space-x-2 text-sm">
                        <Icon name="Plus" size={12} className="text-success" />
                        <span className="text-text-secondary">{factor}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-3 flex items-center space-x-2">
                    <Icon name="AlertTriangle" size={16} />
                    <span>Limiting Factors</span>
                  </h4>
                  <ul className="space-y-2">
                    {methodology?.confidenceFactors?.negative?.map((factor, index) => (
                      <li key={index} className="flex items-center space-x-2 text-sm">
                        <Icon name="Minus" size={12} className="text-warning" />
                        <span className="text-text-secondary">{factor}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdjustmentNotes;